import React, { useRef, useState } from 'react';
import './LandingPage.css';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';

function LandingPage({ onGetStarted, onSignIn, user, onLogout, onStartProTrial, subscriptionInfo }) {
  const fileInputRef = useRef(null);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const handleFileUpload = (files) => {
    if (files && files.length > 0) {
      // Convert FileList to Array and pass to main app
      const fileArray = Array.from(files);
      onGetStarted(fileArray);
    }
  };

  const handleUploadBoxClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    handleFileUpload(e.target.files);
    e.target.value = ''; // Reset input
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    handleFileUpload(e.dataTransfer.files);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (items) {
      const files = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file') {
          files.push(items[i].getAsFile());
        }
      }
      if (files.length > 0) {
        handleFileUpload(files);
      }
    }
  };

  // Add paste event listener to window
  React.useEffect(() => {
    const handleWindowPaste = (e) => {
      // Only handle paste if we're on the landing page and not in an input field
      if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        handlePaste(e);
      }
    };

    window.addEventListener('paste', handleWindowPaste);
    return () => window.removeEventListener('paste', handleWindowPaste);
  }, []);

  const features = [
    {
      icon: '🤖',
      title: 'AI-Powered Enhancement',
      description: 'Transform your notes with advanced AI that adds structure, formatting, and insights.'
    },
    {
      icon: '🎤',
      title: 'Speech-to-Text',
      description: 'Capture thoughts effortlessly with real-time voice transcription.'
    },
    {
      icon: '📁',
      title: 'Smart Organization',
      description: 'Organize notes in custom folders with drag-and-drop functionality.'
    },
    {
      icon: '📸',
      title: 'Multi-Media Support',
      description: 'Attach images, PDFs, and capture photos directly.'
    },
    {
      icon: '☁️',
      title: 'Cloud Sync',
      description: 'Access your notes anywhere with secure cloud storage.'
    },
    {
      icon: '🔒',
      title: 'Secure & Private',
      description: 'Your notes are encrypted and stored securely.'
    }
  ];

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="top-nav">
        <div className="nav-container">
          <div className="logo">
                            <span className="logo-text">Swift Notes</span>
          </div>
          <div className="nav-links">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            {user ? (
              <div className="nav-user-info">
                <div className="nav-user-info-section">
                  <span className="nav-user-email">{user.email}</span>
                  {subscriptionInfo?.isPro && <span className="nav-pro-badge">PRO</span>}
                </div>
                <button className="nav-logout-btn" onClick={onLogout}>Logout</button>
              </div>
            ) : (
              <button className="sign-in-btn" onClick={onSignIn}>Sign In</button>
            )}
            <button className="get-started-btn" onClick={onGetStarted}>Get Started</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <h1 className="main-title">
            Your AI Note-Taking Helper
          </h1>
          <h2 className="sub-title">
                            <span className="highlight">Swift Notes</span> it, <span className="highlight">Ace</span> it!
          </h2>
          
          <div className="upload-area">
            <div className="upload-box" onClick={handleUploadBoxClick} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
              <div className="upload-icon">📝</div>
              <p>Upload Image or PDF to enhance with AI</p>
              <div className="upload-hint">Ctrl + V to paste</div>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              multiple
              style={{ display: 'none' }}
            />
          </div>

          <div className="feature-tags">
            <span className="tag">📝 Notes</span>
            <span className="tag">🎤 Speech</span>
            <span className="tag">🤖 AI Enhancement</span>
            <span className="tag">📁 Organization</span>
            <span className="tag">📸 Multimedia</span>
            <span className="tag">☁️ Cloud Sync</span>
            <span className="tag-more">MORE</span>
          </div>

          <div className="cta-section">
            <h3 className="cta-title">The Only AI Companion You Need to Succeed</h3>
            <p className="cta-subtitle">Unleash the power of Swift Notes AI to organize all your thoughts</p>
            <button className="main-cta" onClick={onGetStarted}>
              Swift Notes AI
            </button>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section className="demo-section" id="demo">
        <div className="demo-container">
          <div className="demo-card">
            <div className="demo-header">
              <span className="demo-title">🤖 AI Enhanced Note</span>
            </div>
            <div className="demo-content">
              <div className="step-indicator">STEP 1</div>
              <div className="demo-text">
                <strong>Meeting Summary</strong><br/>
                The given note is a basic meeting recap. We can enhance it by adding structure and formatting.
                <br/><br/>
                1. Add clear headings and bullet points<br/>
                2. Highlight key action items<br/>
                3. Format with proper spacing
              </div>
              <div className="step-indicator">STEP 2</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="features-section" id="features">
        <div className="container">
          <h2 className="section-heading">Swift Notes AI</h2>
          <p className="section-subtitle">Solve all note-taking challenges using our industry-leading AI technology.</p>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-item">
                <div className="feature-icon">{feature.icon}</div>
                <h4 className="feature-name">{feature.title}</h4>
                <p className="feature-desc">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" id="pricing">
        <div className="container">
          <h2 className="section-heading">Choose Your Plan</h2>
          <div className="pricing-cards">
            <div className="price-card">
              <h3 className="plan-title">Free</h3>
              <div className="price-display">
                <span className="price-amount">$0</span>
                <span className="price-period">/month</span>
              </div>
              <ul className="feature-list">
                <li>✅ Up to 20 notes/month</li>
                <li>📢 With ads</li>
                <li>✅ Basic AI enhancement</li>
                <li>✅ 5 folders</li>
                <li>✅ Unlimited speech-to-text</li>
                <li>✅ File attachments (10MB)</li>
              </ul>
              <button className="plan-btn free-btn" onClick={onGetStarted}>Start Free</button>
            </div>

            <div className="price-card featured-card">
              <div className="popular-badge">Most Popular</div>
              <h3 className="plan-title">Pro</h3>
              <div className="price-display">
                <span className="price-amount">$9.99</span>
                <span className="price-period">/month</span>
              </div>
              <ul className="feature-list">
                <li>✅ Unlimited notes</li>
                <li>🚫 No ads</li>
                <li>✅ Advanced AI enhancement</li>
                <li>✅ Unlimited folders</li>
                <li>✅ Unlimited speech-to-text</li>
                <li>✅ File attachments (10MB)</li>
                <li>✅ Smart categorization</li>
                <li>✅ Advanced search</li>
              </ul>
              {subscriptionInfo?.isPro ? (
                <div className="current-plan-status">
                  <div className="plan-status-badge">✅ Current Plan</div>
                  <div className="plan-status-text">
                    {subscriptionInfo.subscriptionCancelAtPeriodEnd ? 
                      'Canceling at period end' : 
                      'Active Pro Subscription'
                    }
                  </div>
                  {subscriptionInfo.subscriptionExpiry && (
                    <div className="plan-expiry-text">
                      {subscriptionInfo.subscriptionCancelAtPeriodEnd ? 
                        `Access until ${new Date(subscriptionInfo.subscriptionExpiry).toLocaleDateString()}` :
                        `Next billing: ${new Date(subscriptionInfo.subscriptionExpiry).toLocaleDateString()}`
                      }
                    </div>
                  )}
                  <div className="subscription-actions">
                    {subscriptionInfo.subscriptionCancelAtPeriodEnd ? (
                      <button className="reactivate-btn" onClick={() => window.reactivateSubscription?.()}>
                        Reactivate Subscription
                      </button>
                    ) : (
                      <button className="cancel-btn" onClick={() => window.cancelSubscription?.()}>
                        Cancel Subscription
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <button className="plan-btn pro-btn" onClick={onStartProTrial}>Start Pro Trial</button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Gauth Style */}
      <section className="final-cta">
        <div className="container">
          <h2 className="final-title">Swift Notes it, Ace it!</h2>
          <button className="final-btn" onClick={onGetStarted}>Get Started</button>
        </div>
      </section>

      {/* Footer - Gauth Style */}
      <footer className="site-footer">
        <div className="container">
          <div className="footer-brand">
            <div className="footer-main-title">Swift Notes it, Ace it!</div>
          </div>
          
          <div className="footer-grid">
            <div className="footer-col">
              <h4>Company</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#demo">AI Enhancement</a>
              <a href="#features">Speech-to-Text</a>
            </div>
            <div className="footer-col">
              <h4>Legal</h4>
              <a href="#" onClick={(e) => { 
                e.preventDefault(); 
                console.log('Privacy Policy clicked'); 
                setShowPrivacy(true); 
              }}>Privacy Policy</a>
              <a href="#" onClick={(e) => { 
                e.preventDefault(); 
                console.log('Terms of Service clicked'); 
                setShowTerms(true); 
              }}>Terms of Service</a>
            </div>
          </div>
          
          <div className="footer-bottom">
            <div className="footer-copyright">
              &copy; 2025 Swift Notes. All rights reserved.
            </div>
            <div className="footer-social">
              <a href="#" className="social-link" title="Twitter">🐦</a>
              <a href="#" className="social-link" title="LinkedIn">💼</a>
              <a href="#" className="social-link" title="GitHub">💻</a>
              <a href="#" className="social-link" title="Discord">💬</a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Legal Pages Modals */}
      {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
    </div>
  );
}

export default LandingPage; 